import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Factory, Wrench, ClipboardList, GitFork, Plus, ArrowRight, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function ProductionDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await inventoryAPI.dashboard();
        setData(res.data);
      } catch { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;

  const stats = [
    { label: 'Active Work Orders', value: data.active_work_orders || 0, icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-50', link: isAdmin ? '/production/work-orders' : '/production/production-plans' },
    { label: 'Total Work Orders', value: data.total_work_orders || 0, icon: Wrench, color: 'text-slate-600', bg: 'bg-slate-50', link: isAdmin ? '/production/work-orders' : '/production/production-plans' },
    { label: 'Work Centers', value: data.total_work_centers || 0, icon: Factory, color: 'text-blue-600', bg: 'bg-blue-50', link: '/production/work-centers' },
    { label: 'Routes', value: data.total_routes || 0, icon: ClipboardList, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/production/routes' },
  ];

  return (
    <div className="space-y-6" data-testid="production-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Production</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Daily plan, routes, BOM, and subcontracting</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button size="sm" onClick={() => navigate('/production/work-orders')} data-testid="new-wo-btn">
              <Plus className="w-4 h-4 mr-1" /> Work Order
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate('/production/production-plans')} data-testid="daily-plan-btn">
            <CalendarDays className="w-4 h-4 mr-1" /> Daily Plan
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/production/subcontract')} data-testid="subcontract-btn">
            <GitFork className="w-4 h-4 mr-1" /> Subcontract
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <Card key={s.label} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(s.link)}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
